/*
 * File: d:\projects\AutoFiFront\src\Content\RentingTool\RentingTool.tsx
 * Project: d:\projects\AutoFiFront
 * Created Date: Friday March 31st 2023
 * Author: Jonathan Mazat
 * -----
 * Last Modified: Friday March 31st 2023 10:31:25 am
 * Modified By: the developer formerly known as Jonathan Mazat at <you@you.you>
 * -----
 * Copyright (c) 2023 Autobuy SAS
 * -----
 * HISTORY:
 */

import React, { useState, useEffect } from 'react';
import styles from 'Content/RentingTool/RentingTool.module.css';
import translator from 'MultiLanguage/TranslationWrapper';
import SliderTool from 'Content/SliderTool/SliderTool';
import IntelBoard from 'Content/IntelBoard/IntelBoard';
import RentingToolStyled from 'Theme/Component/RentingToolStyled';
import { CssVarsProvider, extendTheme } from '@mui/joy/styles';
import CarService from 'Services/Cars/CarService';

type RentingToolProps = {
    SellPrice: number,
    Mileage: number,
    Contribution: number,
    MonthsNb: number,
    bgColor: string,
    border?: number,
    setSimulation?: Function
};

var first = "#f1a200";
var second = "#BDD138";
var third = "#46abe1";
var fourth = "#ffda00";
var fifth = "#9492f7";
var sixth = "#e35d9e";

const themeMUI = extendTheme({
    components: {
      JoySlider: {
        styleOverrides: {
          root: ({ ownerState, colors }) => ({
            ...(ownerState.color === 'sedan' && {
              "--Slider-trackBackground": fifth,
              "--Slider-thumbColor": fifth,
              "--Slider-thumbBackground":"#fff",
              "&:hover":{
                "--Slider-trackBackground": fifth,
                "--Slider-thumbColor": fifth,
                "--Slider-thumbBackground":"#fff",
              }
            }),
            ...(ownerState.color === 'suv' && {
              "--Slider-trackBackground": third,
              "--Slider-thumbColor": third,
              "--Slider-thumbBackground":"#fff",
              "&:hover":{
                "--Slider-trackBackground": third,
                "--Slider-thumbColor": third,
                "--Slider-thumbBackground":"#fff",
              }
            }),
            ...(ownerState.color === 'citycar' && {
              "--Slider-trackBackground": second,
              "--Slider-thumbColor": second,
              "--Slider-thumbBackground":"#fff",
              "&:hover":{
                "--Slider-trackBackground": second,
                "--Slider-thumbColor": second,
                "--Slider-thumbBackground":"#fff",
              }
            }),
            ...(ownerState.color === 'break' && {
              "--Slider-trackBackground": sixth,
              "--Slider-thumbColor": sixth,
              "--Slider-thumbBackground":"#fff",
              "&:hover":{
                "--Slider-trackBackground": sixth,
                "--Slider-thumbColor": sixth,
                "--Slider-thumbBackground":"#fff",
              }
            }),
            ...(ownerState.color === 'utility' && {
              "--Slider-trackBackground": fourth,
              "--Slider-thumbColor": fourth,
              "--Slider-thumbBackground":"#fff",
              "&:hover":{
                "--Slider-trackBackground": fourth,
                "--Slider-thumbColor": fourth,
                "--Slider-thumbBackground":"#fff",
              }
            }),
            ...(ownerState.color === 'neutre' && {
              "--Slider-trackBackground": first,
              "--Slider-thumbColor": first,
              "--Slider-thumbBackground":"#fff",
              "&:hover":{
                "--Slider-trackBackground": first,
                "--Slider-thumbColor": first,
                "--Slider-thumbBackground":"#fff",
              }
            }),
          }),
        },
      },
    },
  });


function RentingTool (props: RentingToolProps){
    const sellPrice = props.SellPrice;
    const [mileage, setMileage] = useState(props.Mileage);
    const [contribution, setContribution] = useState(props.Contribution);
    const [monthsNB, setMonthNB] = useState(props.MonthsNb);
    const [rentalCost,setRentalCost] = useState(0);
    const amountLeft = sellPrice - contribution;
    const resumePricing = [
        {
            item: "contribution",
            value: contribution,
            unit: "€"
        },
        {
            item: "amountLeft",
            value: amountLeft,
            unit: "€"
        },
        {
            item: "duration",
            value: monthsNB,
            unit: translator.decode(`pricing.month`)
        },
        {
            item: "mileageYear",
            value: mileage,
            unit: `${translator.decode("pricing.mileage")}/${translator.decode("pricing.year")}`
        }
    ];
    const rentalCostItem = {
        item: translator.decode("pricing.rentalCost"),
        value: rentalCost,
        unit: "€"
    };

    function calcFunction(){
        const ret = CarService.getRentalCost(contribution, sellPrice, mileage, monthsNB);
        ret.then((e) => {
            if (e.rent) {
                setRentalCost(e.rent);
                if (props.setSimulation)
                    props.setSimulation({mileage, contribution, monthsNB, rentalCost: e.rent});
            }
        });
    }

    useEffect(() => {
        calcFunction();
    }, []);

    return (
        <React.Fragment>
                <div className={`${styles.pricingTool}`}>
                <CssVarsProvider theme={themeMUI}>
                    <RentingToolStyled border={props.border} bgColor={props.bgColor}>
                        <div className={`${styles.pricingLeft}`}>
                            <h3 className={`title`}> {translator.decode("pricing.sellPrice")} {sellPrice} € </h3>
                            <SliderTool calcFunction={calcFunction} setter={setContribution} value={contribution} step={1} minValue={0} maxValue={sellPrice < 20000 ? Math.trunc(sellPrice * 0.8) : 20000} unit={"€"} title={'contribution'} bgColor={props.bgColor}/>
                            <SliderTool calcFunction={calcFunction} setter={setMonthNB} value={monthsNB} minValue={24} maxValue={60} unit={translator.decode("pricing.month")} step={12} title={'duration'} bgColor={props.bgColor}/>
                            <SliderTool calcFunction={calcFunction} setter={setMileage} value={mileage} minValue={10000} maxValue={25000} unit={translator.decode("pricing.mileage")} unit2={`${translator.decode("pricing.mileage")}/${translator.decode("pricing.year")}`} step={5000} title={'mileageYear'} bgColor={props.bgColor}/>
                        </div>
                    </RentingToolStyled>
                    <IntelBoard border={props.border} items={resumePricing} title={translator.decode("pricing.simulation")} finalItem={rentalCostItem} bgColor={props.bgColor} />
                </CssVarsProvider>
                </div>
        </React.Fragment>
    );
}

export default RentingTool;
